import { Dialog, DialogContent, DialogTitle } from "@mui/material"
import CircularProgress from "@mui/material/CircularProgress"
import { ColDef, ICellRendererParams, ValueGetterParams } from "ag-grid-community"
import { useEffect, useState } from "react"
import { CancelIcon } from "../../icon"
import { PhoneHostImportRecord, PhoneHostImportReport, mapToPhoneHostImportReport } from "../../models/phone-host-import-report.model"
import { PhoneNumberService } from "../../services/PhonenumberService"
import { UtilityService } from "../../services/UtilityService"
import GridComponent from "../shared/grid"
import { GridStatusLabelVariant } from "../shared/constants"
import { StatusRenderer } from "../shared/grid/statusRenderer"

interface IProps {
    Open: boolean
    ReportId: string
    OnClose: () => void
}

const recordColumnDefs: ColDef[] = [
    {
        field: "Name",
        headerName: "Name",
        valueGetter: (params: ValueGetterParams) => params.data?.Name || "--",
        minWidth: 180,
        resizable: true
    },
    {
        field: "Number",
        headerName: "Phone Number",
        valueGetter: (params: ValueGetterParams) => params.data?.Number || "--",
        minWidth: 180,
        resizable: true
    },
    {
        field: "Errors",
        headerName: "Errors",
        cellRenderer: (params: ICellRendererParams) => {
            const errors = params.data?.Errors || {}
            const entries = Object.entries(errors)
            if (!entries.length) return <span>--</span>
            return (
                <div>
                    {entries.map(([key, value]) => (
                        <div key={key}>
                            <strong>{key}</strong>: <span className="d-flex w-break">{String(value)}</span>
                        </div>
                    ))}
                </div>
            )
        },
        autoHeight: true,
        minWidth: 240,
        resizable: true
    }
]

const statusVariant = (status: string) => {
    const value = (status || "").toLowerCase()
    if (value === "completed") {
        return GridStatusLabelVariant.Active
    }
    if (value === "failed") {
        return GridStatusLabelVariant.Danger
    }
    if (value === "incomplete") {
        return GridStatusLabelVariant.Warning
    }
    return GridStatusLabelVariant.Other
}

const RecordGrid = ({ rows, emptyLabel }: { rows: PhoneHostImportRecord[], emptyLabel: string }) => {
    return (
        <div style={{ height: 320 }}>
            <GridComponent
                ColumnDefs={recordColumnDefs}
                RowData={rows}
                OnGridReady={() => { }}
                IsPaginationEnabled={false}
                EmptyLabel={emptyLabel}
                LockVisible={false}
                isHorizontalScroll={true}
            />
        </div>
    )
}

const PhoneHostImportReportDetail = (props: IProps) => {
    const [report, setReport] = useState<PhoneHostImportReport | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [failed, setFailed] = useState<boolean>(false)

    useEffect(() => {
        if (!props.Open || !props.ReportId) return

        setLoading(true)
        setFailed(false)
        setReport(null)
        PhoneNumberService.getSingleBulkImportReport(props.ReportId)
            .then((res) => {
                setReport(mapToPhoneHostImportReport(res.data))
            })
            .catch(() => {
                setFailed(true)
            })
            .finally(() => {
                setLoading(false)
            })
    }, [props.Open, props.ReportId])

    return (
        <Dialog
            open={props.Open}
            onClose={props.OnClose}
            classes={{ paper: "br-2 dialogue-shadow w-100 bg-color-menu-bg" }}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle className="d-flex justify-between align-center p-2">
                <div className="h3-medium color-primary-accent">Import report</div>
                <CancelIcon onClick={props.OnClose} className="c-pointer" />
            </DialogTitle>
            <DialogContent className="p-2">
                {loading && (
                    <div className="d-flex align-center justify-center py-5">
                        <CircularProgress size={36} thickness={4} sx={{ color: "#1450FF" }} />
                    </div>
                )}
                {failed && !loading && (
                    <div className="h4-regular color-text-field-label py-3">
                        Unable to load this import report.
                    </div>
                )}
                {!!report && !loading && (
                    <>
                        <div className="d-flex align-center flex-wrap mb-2">
                            <div className="h3-medium color-primary-accent mr-2">{report.FileName || "Import file"}</div>
                            {!!report.Status && (
                                <StatusRenderer Variant={statusVariant(report.Status) as "active" | "inactive" | "warning" | "danger" | "info" | "other"} Label={report.Status} Id={report.Id} />
                            )}
                        </div>
                        <div className="h5-regular color-text-field-label mb-3">
                            {[
                                report.CreatedBy ? `Imported by ${report.CreatedBy}` : "",
                                report.CreatedDateTime ? UtilityService.convertToDateTimeFormat(report.CreatedDateTime) : ""
                            ].filter(Boolean).join(" · ")}
                        </div>

                        <div className="h4-medium color-primary-accent mb-1">
                            Saved records ({report.SavedRecords.length})
                        </div>
                        <RecordGrid rows={report.SavedRecords} emptyLabel="No numbers were saved in this import." />

                        <div className="h4-medium color-primary-accent mt-3 mb-1">
                            Ignored records ({report.IgnoredRecords.length})
                        </div>
                        <RecordGrid rows={report.IgnoredRecords} emptyLabel="No numbers were ignored in this import." />
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default PhoneHostImportReportDetail
